import { IS_LOCAL } from '@/helpers';
import { getShuffledArray } from '@/helpers/shuffledArray';

const I18N = {
  ko: {
    dateFormat: 'yy.LL.dd',
  },
  en: {
    dateFormat: 'LLL dd, yyyy',
  },
};

const LunarNewYearStore = {
  namespaced: true,
  state() {
    return {
      /** 빙고 보드 */
      bingoBoard: [],
      /** 뱀 공 숫자: 최대 2개 */
      snakeNumber: [],
      /** 유저 빙고권 관련 상태 */
      bingoTicketStatus: {
        tickets: [],
        isCloverPurchaseEnabled: false,
        count: 0,
      },
      /** 오늘의 빙고 숫자 */
      bingoNumber: [],
      /** 빙고 상태 */
      bingoStatus: {
        count: 0,
        lines: [],
      },
      /** 오늘의 빙고 보상 상태 */
      todayBingoReward: {
        usrBingos: 0,
        received: [],
      },
      /** 데일리 미션 상태 */
      dailyMissionStatus: {
        attendance: 0,
        invite: 0,
        subs: 0,
        rentalIncome: 0,
        swap: 0,
      },
      /** 빙고권 사용 내역 */
      bingoTicketHistory: [],
      /** 데일리 미션 상세 */
      dailyMissionDetail: [],
      /** 누적 빙고권 획득 수 */
      totalBingoTicketCnts: 0,
      /** 푸른뱀 상태 */
      snakeStatus: {
        isReceived: false,
        count: 0,
      },
      /** 이전 게임 상태 */
      previousGameStatus: {
        prevUsrBingos: 0,
        prevReceivedOmc: 0,
        isFirstVisit: false,
      },
    };
  },
  getters: {
    /** 누적 빙고권 획득 수량 */
    getDailyMissionDetailSum: (state) => state.dailyMissionDetail.reduce((acc, item) => acc + item.value, 0),
    /** 빙고 보드 한 줄 크기 */
    bingoBoardRowLength: (state) => Math.sqrt(state.bingoBoard.length),
    /** 빙고 보드의 각 칸에 type 추가하기
     * @example
     * 타입: 'normal'| 'snake'
     * 클래스: '' | 'selected' | 'daily' | 'bingo'
     * '': 기본
     * 'selected': 유저가 빙고권으로 뽑은 숫자
     * 'daily': 오늘의 빙고 숫자
     * 'bingo': 빙고 숫자
     */
    bingoBoardWithType: (state) => {
      const bingoBoard = state.bingoBoard;
      const snakeNumber = state.snakeNumber;
      const selectedNumber = state.bingoTicketStatus.tickets;
      const todayNumber = state.bingoNumber;

      const bingoSuccessNumber = state.bingoStatus.lines.reduce((acc, line) => {
        if (line.type === 'horizontal') {
          acc.push(...bingoBoard.slice(line.index * 5, line.index * 5 + 5));
        } else if (line.type === 'vertical') {
          acc.push(...bingoBoard.filter((_, index) => index % 5 === line.index));
        } else if (line.type === 'diagonal') {
          if (line.index === 0) {
            acc.push(...bingoBoard.filter((_, index) => index % 6 === 0));
          } else if (line.index === 1) {
            acc.push(...bingoBoard.filter((_, index) => index % 4 === 0 && index % 24 !== 0));
          }
        }
        return acc;
      }, []);

      const isSnake = (cell) => snakeNumber?.includes(cell);
      const isSelected = (cell) => selectedNumber?.includes(cell);
      const isTodayNumber = (cell) => todayNumber?.includes(cell);
      const isBingoSuccessNumber = (cell) => bingoSuccessNumber?.includes(cell);

      const getType = (cell) => (isSnake(cell) ? 'snake' : 'normal');
      const getClassName = (cell) =>
        isBingoSuccessNumber(cell) ? 'bingo' : isSelected(cell) ? 'selected' : isTodayNumber(cell) ? 'daily' : '';
      return bingoBoard.map((cell) => {
        const type = getType(cell);
        const className = getClassName(cell);

        return { cell, type, className };
      });
    },
  },
  mutations: {
    setBingoBoard(state, bingoBoard) {
      state.bingoBoard = bingoBoard;
    },
    setSnakeNumber(state, snakeNumber) {
      state.snakeNumber = snakeNumber;
    },
    setBingoTicketStatus(state, bingoTicketStatus) {
      state.bingoTicketStatus = bingoTicketStatus;
    },
    setBingoNumber(state, bingoNumber) {
      state.bingoNumber = bingoNumber;
    },
    /**
     * bingoBoard에서 빙고 숫자를 찾아서 상태에 업데이트한다.
     * lines의 index는 0부터 시작한다. 그리고 몇번째 줄인지를 뜻하기 때문에 실제 숫자가 아니다.
     * @param {object} state - 상태
     * @param {{
     *     count: number; // 빙고 개수
     *     lines: { type: 'horizontal' | 'vertical' | 'diagonal', index: number }[];
     *   }} bingoStatus - 빙고 상태
     */
    setBingoStatus(state, bingoStatus) {
      state.bingoStatus = bingoStatus;
    },
    setTodayBingoReward(state, todayBingoReward) {
      state.todayBingoReward = todayBingoReward;
    },
    setBingoTicketHistory(state, history) {
      state.bingoTicketHistory = history;
    },
    setDailyMissionStatus(state, status) {
      state.dailyMissionStatus = status;
    },
    setDailyMissionDetail(state, detail) {
      state.dailyMissionDetail = detail;
    },
    setSnakeStatus(state, status) {
      state.snakeStatus = status;
    },
    setPreviousGameStatus(state, status) {
      state.previousGameStatus = status;
    },
  },
  actions: {
    /** fetch 메서드
     * @param {any} _ - 사용하지 않음
     * @param {{ method: 'get' | 'post' | 'put' | 'delete', url: string, params: object }} - 요청 데이터
     * @returns {Promise<{
     *   data: object;
     *   status: number;
     *   statusText: string;
     *   headers: object;
     *   config: object;
     *   request: object;
     * }>}
     *
     * @example
     * const response = await dispatch('fetch', {
     *   method: 'get',
     *   url: '/lunar-new-year/bingo',
     *   params: {
     *     userId: 1,
     *   },
     * });
     * ** response.data로 꺼내서 사용해야 함. **
     * 만약 데이터를 굳이 정제하거나 커스텀 할 필요 없으면, data 자체를 넘겨도 됨.
     *
     * 메서드 순서
     * get > fetch
     * post > submit
     * put > update
     * patch > modify
     * delete > remove
     */
    async fetch(_, { method, url, params }) {
      const reqData = method === 'get' ? { params: { ...params } } : params;
      const response = await this._vm.$axios[method](`${process.env.VUE_APP_TEST_SERVER}${url}`, reqData);
      return response;
    },
    /**
     * 생성된 빙고보드판을 서버에 저장
     * @param {number[]} bingoBoard - 빙고보드판
     * @returns {Promise<null>}
     */
    async postBingoBoard({ dispatch }, bingoBoard) {
      // 서버에 빙고보드 판을 저장한다.
      const response = await dispatch('fetch', {
        method: 'post',
        url: '/lunar-new-year/bingo/board',
        params: {
          bingoBoard,
        },
      });

      return response;
    },
    /**
     * 빙고보드 판을 생성
     * @returns {Promise<void>}
     * */
    async submitBingoBoard({ commit, dispatch }) {
      // 빙고보드 판을 생성한다.
      const bingoBoard = getShuffledArray(50).slice(0, 25);

      // 테스트용 빙고보드 판 고정 (1부터 50까지 숫자 중 25개 배열 sample)
      // const bingoBoard = [
      //   23, 47, 22, 1, 21, 5, 17, 37, 3, 27, 44, 46, 40, 30, 25, 19, 26, 32, 11, 28, 12, 45, 41, 36, 43,
      // ];
      // 서버에 빙고보드 판을 저장한다.
      await dispatch('postBingoBoard', bingoBoard);
      // 빙고보드 판을 상태에 저장한다.
      return commit('setBingoBoard', bingoBoard);
    },
    /**
     * 서버에서 빙고보드 판을 받아온다.
     * @returns {Promise<{
     *  data: number[];
     *  }>}
     * */
    async getBingoBoard({ dispatch }) {
      // 서버에서 빙고보드 판을 받아온다.
      // if (IS_LOCAL) return { data: [] };
      if (IS_LOCAL) return { data: Array.from({ length: 25 }, (_, index) => index + 1) };

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/bingo/board',
      });
      return response;
    },
    /** 빙고보드 판을 상태에 업데이트 한다. */
    async fetchBingoBoard({ commit, dispatch }) {
      // 받아온 빙고보드를 상태에 업데이트 한다.
      const { data } = await dispatch('getBingoBoard');
      return commit('setBingoBoard', data);
    },
    /** 빙고보드 판을 생성하거나 설정한다. */
    async submitOrFetchBingoBoard({ commit, dispatch }) {
      // 서버에서 빙고보드 판을 받아온다.
      const { data } = await dispatch('getBingoBoard');
      // 받아온 빙고보드 판이 있으면 그걸 사용한다. (Early Return)
      if (data.length !== 0) return commit('setBingoBoard', data);
      // 없으면 랜덤으로 빙고보드 판을 생성한다.
      return dispatch('submitBingoBoard');
    },
    /**
     * 뱀 숫자 조회
     * @returns {Promise<{
     *  data: number[];
     * }>}
     */
    async getSnakeNumber({ state, dispatch }) {
      if (IS_LOCAL) {
        return {
          data: state.bingoBoard.slice(0, 2),
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/special-balls',
      });
      return response;
    },
    /** 뱀 숫자 상태 업데이트 */
    async fetchSnakeNumber({ commit, dispatch }) {
      const { data } = await dispatch('getSnakeNumber');

      return commit('setSnakeNumber', data);
    },
    /**
     * 빙고권 상태 가져오기
     * @returns {Promise<{
     *  tickets: number[];
     *  isCloverPurchaseEnabled: boolean;
     *  count: number;
     *  }>}
     */
    async getBingoTicketStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            tickets: [1, 2, 50, 5, 3, 29],
            isCloverPurchaseEnabled: true,
            count: 20,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/tickets',
      });
      return response;
    },
    /** 빙고권 상태를 상태에 업데이트한다. */
    async fetchBingoTicketStatus({ commit, dispatch }) {
      const { data } = await dispatch('getBingoTicketStatus');

      return commit('setBingoTicketStatus', data);
    },
    /**
     * 빙고권 사용
     * @returns {Promise<null>}
     */
    async postBingoTicketUse({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: 18,
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/lunar-new-year/tickets',
      });
      return response;
    },
    /**
     * 빙고권 사용 후 완료 처리
     * @returns {Promise<number | Error & { message: { title: string, content: string } }>}
     * @example{
     * // success
     * null
     * // error
     * Error {
     *   "message": {
     *     "title": "",
     *     "content": ""
     *   }
     * }
     */
    async submitBingoTicketUse({ dispatch }) {
      try {
        const { data } = await dispatch('postBingoTicketUse');

        return data;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchBingoTicketStatus');
        dispatch('fetchBingoStatus');
        dispatch('fetchTodayBingoReward');
        dispatch('fetchSnakeStatus');
      }
    },
    /**
     * 빙고권 구매
     * @returns {Promise<{
     * data: number;
     * }>}
     * */
    async postBingoTicketPurchase({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: 28,
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/lunar-new-year/clover',
      });
      return response;
    },
    /**
     * 푸른 뱀 보상 받기 후 완료 처리
     * @returns {Promise<number | Error & { message: { title: string, content: string } }>}
     * @example{
     * // success
     * null
     * // error
     * Error {
     *   "message": {
     *     "title": "",
     *     "content": ""
     *   }
     * }
     */
    async submitBingoTicketPurchase({ dispatch }) {
      try {
        const { data } = await dispatch('postBingoTicketPurchase');

        return data;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchBingoTicketStatus');
        dispatch('fetchBingoStatus');
        dispatch('fetchTodayBingoReward');
        dispatch('fetchSnakeStatus');
      }
    },
    /** 오늘의 빙고 숫자를 가져온다
     * @returns {Promise<{
     *  data: number[] || string[];
     * }>}
     */
    async getBingoNumber({ dispatch }) {
      if (IS_LOCAL) return { data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] };
      // if (IS_LOCAL) return { data: ['1', '?', '?', '?', '?', '?', '?', '?', '?', '?', '?', '?', '?', '?', '?'] };

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/bingo/numbers',
      });
      return response;
    },
    /** 오늘의 빙고 숫자를 상태에 업데이트한다. */
    async fetchBingoNumber({ commit, dispatch }) {
      const { data: bingoNumber } = await dispatch('getBingoNumber');

      return commit('setBingoNumber', bingoNumber);
    },
    /** 빙고 상태를 가져온다
     * @returns {Promise<{
     *   data: {
     *     count: number; // 빙고 개수
     *     lines: { type: 'horizontal' | 'vertical' | 'diagonal', index: number }[];
     *   };
     *   status: number;
     *   statusText: string;
     *   headers: object;
     *   config: object;
     *   request: object;
     * }>}
     *
     * @example
     * horizontal: 가로 index는 0부터 시작
     * vertical: 세로 index는 0부터 시작
     * diagonal index 0: 왼쪽 위에서 오른쪽 아래 대각선
     * diagonal index 1: 오른쪽 위에서 왼쪽 아래 대각선
     */
    async getBingoStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            count: 2,
            lines: [
              // { type: 'horizontal', index: 0 },
              { type: 'horizontal', index: 1 },
              // { type: 'horizontal', index: 2 },
              // { type: 'horizontal', index: 3 },
              // { type: 'horizontal', index: 4 },
              // { type: 'vertical', index: 0 },
              // { type: 'vertical', index: 1 },
              // { type: 'vertical', index: 2 },
              // { type: 'vertical', index: 3 },
              // { type: 'vertical', index: 4 },
              // { type: 'diagonal', index: 0 },
              { type: 'diagonal', index: 1 },
            ],
          },
        };
      }
      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/bingo',
      });
      return response;
    },
    /** 빙고 상태를 상태에 업데이트한다. */
    async fetchBingoStatus({ commit, dispatch }) {
      const { data } = await dispatch('getBingoStatus');

      commit('setBingoStatus', data);
      return;
    },
    /**
     * 오늘의 빙고 보상 상태 조회
     * @returns {Promise<{
     *  usrBingos: number;
     *  received: number[];
     *  }>}
     */
    async getTodayBingoReward({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            usrBingos: 2,
            received: [1],
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/reward',
      });
      return response;
    },
    /**
     * 오늘의 빙고 보상 상태 조회
     */
    async fetchTodayBingoReward({ dispatch, commit }) {
      const { data: todayBingoReward } = await dispatch('getTodayBingoReward');

      commit('setTodayBingoReward', todayBingoReward);
      return;
    },
    /**
     * 오늘의 빙고 보상 받기
     * @param {number} index - 빙고 개수
     * @returns {Promise<{
     * reward: number;
     * }>}
     */
    async postTodayBingoReward({ dispatch }, index) {
      if (IS_LOCAL) {
        return {
          data: 15,
        };
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/lunar-new-year/reward/bingo',
        params: {
          bingo: index,
        },
      });
      return response;
    },
    /**오늘의 빙고 보상 받기 후 완료 처리
     * @returns {Promise<{reward: number} | Error & { message: { title: string, content: string } }>}
     * @example{
     * // success
     * "reward": 15
     * // error
     * Error {
     *   "message": {
     *     "title": "",
     *     "content": ""
     *   }
     * }
     */
    async submitTodayBingoReward({ dispatch }, index) {
      try {
        const { data: result } = await dispatch('postTodayBingoReward', index);

        return result;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchTodayBingoReward');
      }
    },
    /**
     * 데일리 미션 상태 조회
     * @returns {Promise<{
     *   invite: number;
     *   subs: number;
     *   rentalIncome: number;
     *   swap: number;
     * }>}
     */
    async getDailyMissionStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            invite: 4,
            subs: 2,
            rentalIncome: 1,
            swap: 1,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/mission',
      });
      return response;
    },
    /**
     * 데일리 미션 상태 조회
     */
    async fetchDailyMissionStatus({ dispatch, commit }) {
      const { data: dailyMissionStatus } = await dispatch('getDailyMissionStatus');

      commit('setDailyMissionStatus', dailyMissionStatus);
      return;
    },
    /**
     * 데일리 미션 상태 조회
     */
    async fetchEvent1Status({ dispatch }) {
      await dispatch('fetchDailyMissionStatus');
      await dispatch('fetchBingoNumber');
      return;
    },
    /**
     * 데일리 미션 상세 내역 조회
     * @returns {Promise<{
     *  date: string;
     *  mission: string;
     *  value: number;
     * }[]>}
     */
    async getDailyMissionDetail({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: [
            {
              date: '2023-01-01T00:00:00',
              mission: '출석 체크 완료',
              value: 1,
            },
            {
              date: '2023-01-02T00:00:00',
              mission: '출석 체크 완료',
              value: 4,
            },
          ],
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/mission/detail',
      });
      return response;
    },
    /**
     * 데일리 미션 상세 조회
     */
    async fetchDailyMissionDetail({ dispatch, commit, rootState }) {
      const { data: dailyMissionDetail } = await dispatch('getDailyMissionDetail');

      const { lang } = rootState;

      const format = I18N[lang].dateFormat;
      const formattedDailyMissionDetail = dailyMissionDetail?.map((item) => ({
        ...item,
        date: this._vm.$tl(item.date, lang).toFormat(format),
      }));

      commit('setDailyMissionDetail', formattedDailyMissionDetail);
      return;
    },
    /**
     * 빙고권 사용 내역 조회
     * @returns {Promise<{
     *  date: string;
     *  number: number;
     * }[]>}
     */
    async getBingoTicketHistory({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: [
            {
              date: '2023-01-01T00:00:00',
              number: 1,
            },
            {
              date: '2023-01-02T00:00:00',
              number: 1,
            },
          ],
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/bingo/detail',
      });
      return response;
    },
    /**
     * 빙고권 사용 내역 조회
     */
    async fetchBingoTicketHistory({ dispatch, commit, rootState }) {
      const { data: bingoTicketHistory } = await dispatch('getBingoTicketHistory');

      const { lang } = rootState;

      const format = I18N[lang].dateFormat;
      const formattedBingoTicketHistory = bingoTicketHistory?.map((item) => ({
        ...item,
        date: this._vm.$tl(item.date, lang).toFormat(format),
      }));

      commit('setBingoTicketHistory', formattedBingoTicketHistory);
      return;
    },
    /**
     * 푸른 뱀 상태 조회
     * @returns {Promise<{
     *  isReceivable: boolean;
     *  isReceived: boolean;
     *  count: number;
     *  }[]>}
     */
    async getSnakeStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            isReceived: false,
            count: 6,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/special-balls',
      });
      return response;
    },
    /**
     * 푸른 뱀 상태 업데이트
     */
    async fetchSnakeStatus({ dispatch, commit }) {
      const { data: snakeStatus } = await dispatch('getSnakeStatus');

      commit('setSnakeStatus', snakeStatus);
      return;
    },
    /**
     * 푸른 뱀 보상 받기
     * @returns {Promise<null>}
     */
    async postSnakeReward({ dispatch }) {
      if (IS_LOCAL) {
        return null;
      }

      const response = await dispatch('fetch', {
        method: 'post',
        url: '/lunar-new-year/reward/special-balls',
      });
      return response;
    },
    /**
     * 푸른 뱀 보상 받기 후 완료 처리
     * @returns {Promise<number | Error & { message: { title: string, content: string } }>}
     * @example{
     * // success
     * null
     * // error
     * Error {
     *   "message": {
     *     "title": "간식 세트를 완성할 수 없습니다",
     *     "content": "완성하기 위한 포크가 모자랍니다. 미션을 통해 포크를 모아보세요."
     *   }
     * }
     */
    async submitSnakeReward({ dispatch }) {
      try {
        await dispatch('postSnakeReward');

        return true;
      } catch (error) {
        if (!error.response?.data?.message) throw error;

        return error.response.data.message;
      } finally {
        dispatch('fetchSnakeStatus');
      }
    },
    /**
     * 이전 게임 상태 조회
     * @returns {Promise<{
     *  prevUsrBingos: number;
     *  prevReceivedOmc: number;
     *  isFirstVisit: boolean;
     *  }[]>}
     */
    async getPreviousGameStatus({ dispatch }) {
      if (IS_LOCAL) {
        return {
          data: {
            prevUsrBingos: 4,
            prevReceivedOmc: 2,
            isFirstVisit: false,
          },
        };
      }

      const response = await dispatch('fetch', {
        method: 'get',
        url: '/lunar-new-year/status/previous-game',
      });
      return response;
    },
    /**
     * 이전 게임 상태 업데이트
     */
    async fetchPreviousGameStatus({ dispatch, commit }) {
      const { data: previousGameStatus } = await dispatch('getPreviousGameStatus');

      commit('setPreviousGameStatus', previousGameStatus);
      return;
    },
  },
};

export default LunarNewYearStore;
